
import React, { type JSX } from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import Quill, { type EmitterSource } from 'quill';
import type { Delta as DeltaType } from 'quill/core';
import Delta from 'quill-delta';
import QuillResizeImage from 'quill-resize-image';
import QuillSyntax from 'quill/modules/syntax';
import TableUp, { defaultCustomSelect, TableAlign, TableMenuContextmenu, TableResizeBox, TableResizeScale, TableSelection, TableVirtualScrollbar } from 'quill-table-up';
import 'quill-table-up/index.css';
import 'quill-table-up/table-creator.css';

Quill.register('modules/resize', QuillResizeImage);
Quill.register('modules/syntax', QuillSyntax);
Quill.register({ [`modules/${TableUp.moduleName}`]: TableUp }, true);

import { PromiseCompletion } from '../classes/PromiseCompletion';
import { socketClientService } from '../services/SocketClientService';
import { apiService, HttpError } from '../services/ApiService';

const icons = Quill.import("ui/icons") as Partial<Record<string, string>>;
icons["undo"] = 'UNDO';

//https://github.com/quilljs/quill/issues/2052

export type EditorProps = {
  noteId: string;
  sharedView: boolean;
  notFoundView?: React.ComponentType<any>;
  accessDeniedView?: React.ComponentType<any>;
  onDataLoaded?: (data: NoteResponse) => void;
};

type IQuillHistoryModule = {
  history?: {
    clear: () => void;
  }
}

type NoteEvent = {
  delta: string;
}

export type NoteResponse = {
  name: string;
  content: string;
  sharedAsReadOnly: boolean;
};

/*
const Size = Quill.import('attributors/style/size') as any;
Size.whitelist = ['10px', false, 'large', 'huge', '4.242px'];
Quill.register(Size, true);
*/

@observer
export default class Editor extends React.Component<EditorProps> {
  private _changes = new Delta();
  private _isUpdatingFromServer = false;
  private _editorContainerRef: React.RefObject<HTMLDivElement> = React.createRef();
  private _quillInstance: Quill | null = null;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _saveInterval = 0;
  private _isLoaded = false;
  private _initialContent: string = '';
  @observable private _canEdit = false;
  @observable private _notFound = false;
  @observable private _accessDenied = false;

  constructor(props: EditorProps){
    super(props);
    makeObservable(this);
  }

  async componentDidMount(): Promise<void>{
    const { noteId } = this.props;
    socketClientService.subscribe<NoteEvent>('note', noteId, 'note-update', this._onNoteUpdate);
    window.addEventListener('beforeunload', this._onBeforeUnload);
    this._saveInterval = window.setInterval(this._saveCurrentChanges, 500);
    await this._loadNote(noteId);
  }

  componentDidUpdate(_prevProps: EditorProps): void {
    // Reinitialize Quill if canEdit state changed and container is available
    if (this._editorContainerRef.current && !this._quillInstance) {
      this._initializeQuill();
    }
  }

  componentWillUnmount(): void {
    const { noteId } = this.props;
    socketClientService.unsubscribe('note-update', noteId, this._onNoteUpdate);
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    window.clearInterval(this._saveInterval);
    this._saveInterval = 0;
    this._saveCurrentChanges();
    if (this._quillInstance) {
      this._quillInstance.off('text-change', this._onTextChange);
      this._quillInstance = null;
    }
  }

  render(): JSX.Element{
    const { notFoundView, accessDeniedView } = this.props;

    if (this._notFound && notFoundView){
      return React.createElement(notFoundView);
    }
    if (this._accessDenied && accessDeniedView){
      return React.createElement(accessDeniedView);
    }

    let className = 'note-details';
    if (!this._canEdit){
      className += ' note-details-readonly';
    }

    return (
      <div className={className}>
        <div id="snow-container">
          <div ref={this._editorContainerRef} onKeyDown={this._onEditorKeyDown} />
        </div>
      </div>
    );
  }

  private async _loadNote(activeNoteId: string): Promise<void>{
    this._notFound = false;
    this._accessDenied = false;
    try{

      const data = await apiService.post<NoteResponse>('/api/note/' + activeNoteId, { saveAsCurrent: 'true' }, {
        loader: this._loader,
        defaultErrorHandling: false     
      });

      let canEdit = !data.sharedAsReadOnly;
      if (data.sharedAsReadOnly && !this.props.sharedView){
        canEdit = true;
      }

      this._canEdit = canEdit;
      this._initialContent = data.content;
      this.props.onDataLoaded?.(data);
      this._isLoaded = true;

    } catch (error) {
      const httpError = error as HttpError;
      if (httpError?.code === 404){
        this._notFound = true;
      } else if (httpError?.code === 403){
        this._accessDenied = true;
      } else {
        apiService.defaultErrorHandler(error as HttpError);
      }
    }
  }

  private _initializeQuill(): void {
    if (!this._editorContainerRef.current || this._quillInstance) return;

    const icons = Quill.import("ui/icons") as Partial<Record<string, string>>;
    icons["undo"] = `<?xml version="1.0"?>
<svg width="18px" height="18px" viewBox="10 10 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
<path class="ql-fill ql-stroke" fill-opacity="1" stroke-width="0.2" stroke-linejoin="round" d="M 52.7514,25.076C 53.4111,25.7534 54.0258,26.4933 54.5953,27.296C 55.1649,28.0987 55.6564,28.9607 56.0698,29.8821C 56.4832,30.8035 56.8065,31.7766 57.0396,32.8014C 57.2727,33.8262 57.3892,34.8883 57.3892,35.9878C 57.3892,37.0566 57.276,38.1517 57.0495,39.2733C 56.823,40.3948 56.4491,41.5394 55.928,42.7071C 55.4068,43.8748 54.713,45.07 53.8465,46.2927C 52.9801,47.5154 51.9069,48.7601 50.6271,50.0267C 50.2137,50.4358 49.7431,50.902 49.2153,51.4253C 48.6875,51.9487 48.1278,52.5029 47.5363,53.0878C 46.9447,53.6728 46.34,54.2742 45.722,54.8922C 45.1041,55.5101 44.4983,56.1072 43.9045,56.6833C 42.5323,58.0424 41.1029,59.4586 39.6163,60.9319L 35.3545,56.6239C 36.8235,55.1638 38.2441,53.7542 39.6163,52.3951C 40.1969,51.819 40.7994,51.2252 41.424,50.6139C 42.0485,50.0025 42.6565,49.4011 43.2481,48.8095C 43.8396,48.218 44.3993,47.6605 44.9271,47.1371C 45.4549,46.6138 45.9255,46.1476 46.3389,45.7385C 48.0146,44.0584 49.2758,42.3904 50.1224,40.7345C 50.969,39.0786 51.3924,37.4678 51.3924,35.9021C 51.3924,34.719 51.1516,33.5777 50.67,32.4781C 50.1884,31.3786 49.4528,30.3406 48.4632,29.3642C 48.2169,29.1179 47.9112,28.8848 47.5462,28.6649C 47.1811,28.445 46.7688,28.2526 46.3092,28.0877C 45.8496,27.9227 45.3482,27.7886 44.805,27.6852C 44.2619,27.5819 43.6868,27.5302 43.0799,27.5302C 42.4641,27.5302 41.8198,27.5929 41.1469,27.7182C 40.474,27.8436 39.7933,28.0503 39.105,28.3384C 38.4167,28.6264 37.7218,29.0036 37.0203,29.4698C 36.3188,29.936 35.6316,30.5056 34.9587,31.1785L 30.466,35.6976L 37.4986,42.75L 19,42.75L 19,24.5417L 25.9931,31.4094L 30.5056,26.8705C 31.5083,25.7446 32.5463,24.8275 33.6194,24.1194C 34.6926,23.4113 35.7646,22.8616 36.8356,22.4701C 37.9065,22.0787 38.972,21.8148 40.0319,21.6785C 41.0919,21.5421 42.1079,21.474 43.0799,21.474C 44.087,21.474 45.0579,21.563 45.9925,21.7411C 46.9271,21.9193 47.8068,22.17 48.6314,22.4932C 49.4561,22.8165 50.2148,23.198 50.9075,23.6378C 51.6002,24.0777 52.2148,24.5571 52.7514,25.076 Z " />
</svg>`
    icons["redo"] = `<?xml version="1.0"?>
<svg width="18px" height="18px" viewBox="10 10 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
<path class="ql-fill ql-stroke" fill-opacity="1" stroke-width="0.2" stroke-linejoin="round" d="M 23.2486,25.076C 22.5889,25.7534 21.9742,26.4933 21.4047,27.296C 20.8351,28.0987 20.3436,28.9607 19.9302,29.8821C 19.5168,30.8035 19.1935,31.7766 18.9604,32.8014C 18.7273,33.8262 18.6107,34.8883 18.6107,35.9878C 18.6107,37.0566 18.724,38.1517 18.9505,39.2733C 19.177,40.3948 19.5508,41.5394 20.072,42.7071C 20.5932,43.8748 21.287,45.07 22.1535,46.2927C 23.0199,47.5154 24.093,48.7601 25.3729,50.0267C 25.7863,50.4358 26.2569,50.902 26.7847,51.4253C 27.3125,51.9487 27.8721,52.5029 28.4637,53.0878C 29.0552,53.6728 29.66,54.2742 30.2779,54.8922C 30.8959,55.5101 31.5017,56.1072 32.0955,56.6833C 33.4677,58.0424 34.8971,59.4586 36.3837,60.9319L 40.6455,56.6239C 39.1765,55.1638 37.7559,53.7542 36.3837,52.3951C 35.8031,51.819 35.2006,51.2252 34.576,50.6139C 33.9515,50.0025 33.3434,49.4011 32.7519,48.8095C 32.1603,48.218 31.6007,47.6605 31.0729,47.1371C 30.5451,46.6138 30.0745,46.1476 29.6611,45.7385C 27.9854,44.0584 26.7242,42.3904 25.8776,40.7345C 25.0309,39.0786 24.6076,37.4678 24.6076,35.9021C 24.6076,34.719 24.8484,33.5777 25.33,32.4781C 25.8116,31.3786 26.5472,30.3406 27.5368,29.3642C 27.7831,29.1179 28.0887,28.8848 28.4538,28.6649C 28.8188,28.445 29.2312,28.2526 29.6908,28.0877C 30.1504,27.9227 30.6518,27.7886 31.1949,27.6852C 31.7381,27.5819 32.3132,27.5302 32.9201,27.5302C 33.5359,27.5302 34.1802,27.5929 34.8531,27.7182C 35.526,27.8436 36.2066,28.0503 36.8949,28.3384C 37.5833,28.6264 38.2782,29.0036 38.9797,29.4698C 39.6812,29.936 40.3684,30.5056 41.0413,31.1785L 45.534,35.6976L 38.5014,42.75L 57,42.75L 57,24.5417L 50.0069,31.4094L 45.4944,26.8705C 44.4916,25.7446 43.4537,24.8275 42.3805,24.1194C 41.3074,23.4113 40.2353,22.8616 39.1644,22.4701C 38.0934,22.0787 37.028,21.8148 35.968,21.6785C 34.9081,21.5421 33.8921,21.474 32.9201,21.474C 31.9129,21.474 30.942,21.563 30.0074,21.7411C 29.0728,21.9193 28.1932,22.17 27.3686,22.4932C 26.5439,22.8165 25.7852,23.198 25.0925,23.6378C 24.3998,24.0777 23.7852,24.5571 23.2486,25.076 Z " />
</svg>`;

    const modules: Record<string, any> = {
      toolbar: [],
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true
      },
      syntax: {
        hljs: window.hljs,
        languages: [
          { key: 'plaintext', label: 'Plain Text' },
          { key: 'javascript', label: 'JavaScript' },
          { key: 'typescript', label: 'TypeScript' },
          { key: 'python', label: 'Python' },
          { key: 'java', label: 'Java' },
          { key: 'csharp', label: 'C#' },
          { key: 'cpp', label: 'C++' },
          { key: 'c', label: 'C' },
          { key: 'php', label: 'PHP' },
          { key: 'ruby', label: 'Ruby' },
          { key: 'go', label: 'Go' },
          { key: 'rust', label: 'Rust' },
          { key: 'swift', label: 'Swift' },
          { key: 'kotlin', label: 'Kotlin' },
          { key: 'sql', label: 'SQL' },
          { key: 'bash', label: 'Bash' },
          { key: 'shell', label: 'Shell' },
          { key: 'xml', label: 'XML/HTML' },
          { key: 'css', label: 'CSS' },
          { key: 'scss', label: 'SCSS' },
          { key: 'less', label: 'Less' },
          { key: 'json', label: 'JSON' },
          { key: 'yaml', label: 'YAML' },
          { key: 'markdown', label: 'Markdown' },
          { key: 'diff', label: 'Diff' },
        ],
        interval: 1000
      }
    };

    if (this._canEdit) {
      modules.toolbar = {
        container: [
          [{ 'size': ['10px', false, 'large', 'huge', '4.242px'] }],
          ['bold', 'italic', 'strike', 'code-block', 'blockquote'],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'script': 'super' }, { 'script': 'sub' }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
          [{ 'align': [] }],
          ['link', 'image'],
          [{ [TableUp.toolName]: [] }],
          ['clean', 'undo', 'redo'],
        ],
        handlers: {
          'undo': () => {
            if (!this._quillInstance) return;
            (this._quillInstance as unknown as {history: {undo: () => void}}).history.undo();
          },
          'redo': () => {
            if (!this._quillInstance) return;
            (this._quillInstance as unknown as {history: {redo: () => void}}).history.redo();
          },
        }
      };
      
      modules.resize = {
        locale: {}
      };

      modules[TableUp.moduleName] = {
        customSelect: defaultCustomSelect,
        modules: [
          { module: TableVirtualScrollbar },
          { module: TableAlign },
          { module: TableResizeBox },
          { module: TableResizeScale },
          { module: TableSelection },
          { module: TableMenuContextmenu },
        ],
      };
    }

    this._quillInstance = new Quill(this._editorContainerRef.current, {
      theme: 'snow',
      modules,
      readOnly: !this._canEdit
    });

    this._quillInstance.on('text-change', this._onTextChange);
    
    // Set initial content if available
    if (this._initialContent) {
      this._updateFromData(this._initialContent, false);
      this._initialContent = '';
    }
  }

  private _updateFromData(delta: string | DeltaType, update: boolean, source?: EmitterSource) {
    this._isUpdatingFromServer = true;
    try {
      let quillDelta: DeltaType;

      if (typeof delta === "string") {
        if (delta){
          quillDelta = JSON.parse(delta);
        } else {
          quillDelta = new Delta();
        }
      } else {
        quillDelta = delta as DeltaType;
      }

      if (update) {
        this._quillInstance?.updateContents(quillDelta, source);
      } else {
        //https://github.com/quilljs/quill/issues/1537
        this._quillInstance?.setText("");
        this._quillInstance?.setContents(quillDelta);
        (this._quillInstance as IQuillHistoryModule).history?.clear();
      }
    } finally {
      this._isUpdatingFromServer = false;
    }
  }

  @action.bound
  private _onEditorKeyDown(e: React.KeyboardEvent<HTMLDivElement>){
    if (e.ctrlKey || e.metaKey) {
      switch (e.keyCode) {
        case 115: // ctrl+s
        case 83: // ctrl+S
          e.preventDefault();
          this._saveChanges();

          break;
      }
    }
  }

  @action.bound
  private _onBeforeUnload() {
    if (this._changes.length() > 0) {
      return 'There are unsaved changes. Are you sure you want to leave?';
    }
    return void 0;
  }

  private _saveChanges(){
    socketClientService.emit('note-changed', {
      noteId: this.props.noteId,
      delta: JSON.stringify(this._changes),
      fullDelta: JSON.stringify(this._quillInstance?.getContents())
    });

    this._changes = new Delta();
  }

  @action.bound
  private _saveCurrentChanges() {
    if (this._changes.length() > 0) {
      this._saveChanges();
    }
  }

  @action.bound
  private _onTextChange(delta: DeltaType, _oldDelta: DeltaType, source: EmitterSource){
    if (this._isUpdatingFromServer || !this._isLoaded || source !== 'user') return;
    this._changes = this._changes.compose(delta);
  }

  @action.bound
  private _onNoteUpdate(data: any){
    this._updateFromData(JSON.parse(data.delta), true, "api");
  }
}